/* eslint-disable react/prop-types */
import * as eventsGuideStyles from '../styles/events/events-guide.module.scss';
import * as popUpStyles from '../styles/layout/search-pop-up.module.scss';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getBodyEvents, getHeaderEvents } from '../utils/guides';
import EventGuideLayout from '../components/layout/templates/EventGuideLayout';
import HeaderCarrousel from '../components/shared/carrousels/HeaderCarrousel';
import InfinitePagination from '../components/shared/InfinitePagination';
import Pagination from '../components/shared/Pagination';
import Results from '../components/evenements/Results';
import SearchCriterias from '../components/evenements/SearchCriterias';
import Seo from '../seo/seo';
import _ from 'lodash';
import algoliasearch from 'algoliasearch';
import { getSEOInformations } from '../utils/parsing';
import { graphql, Slice } from 'gatsby';
import { useTranslation, getI18n } from 'react-i18next';
import dompurify from 'isomorphic-dompurify';
import {dompurifyConfig, setupi18n} from '../utils/utils';
import '../services/i18n';

export const Head = ({ data, pageContext, location }) => {

  const {locale, generalSeo, websiteSeo, name } = pageContext;
  const {
    canonical_link,
    pageSeo,
    metaDescription,
  } = getSEOInformations(data);

  return (
    <Seo
      canonicalLink={canonical_link}
      generalSeo={generalSeo}
      language={locale}
      location={location}
      metaDescription={metaDescription}
      pageSeo={pageSeo}
      type={pageContext.pageType}
      website={name}
      websiteSeo={websiteSeo}
    />
  );
};

export default function EventGuide ({ data, pageContext }) {
  const {
    websiteRoot,
    breadcrumbs,
    pagination,
    guideHash: searchGuideID,
    locale
  } = pageContext;
  setupi18n(locale, getI18n());
  const guide = data.cmsPortalEventGuide;
  const bodyEvts = data.events.nodes;
  const display = guide?.display || 'DATE';
  const { content, header_events: headerEvts, events_per_page } = guide;
  const sanitizer = dompurify.sanitize;
  const {t} = useTranslation();

  const client = useMemo(
    () =>
      algoliasearch(
        process.env.GATSBY_ALGOLIA_APP_ID,
        process.env.GATSBY_ALGOLIA_SEARCH_KEY
      ),
    []
  );

  // On le laisse comme ça pour l'instant; Il faut configurer des indexes en fonction du type de tri

  const indexNameBySearchType = useMemo(
    () => ({
      EVENTS_TO_COME: `${process.env.GATSBY_ALGOLIA_INDEX_NAME}_start_date_timestamp_asc`,
      PAST_EVENTS: `${process.env.GATSBY_ALGOLIA_INDEX_NAME}_start_date_timestamp_desc`,
    }),
    []
  );

  const headerEvents = React.useMemo(
    () => getHeaderEvents(headerEvts, locale, websiteRoot),
    [headerEvts, locale, websiteRoot]
  );
  const bodyEvents = React.useMemo(
    () => getBodyEvents(bodyEvts, locale, websiteRoot),
    [bodyEvts, locale, websiteRoot]
  );

  const [websiteEvents, setWebsiteEvents] = useState(bodyEvents);
  const [categories, setCategories] = React.useState([{ id: 'EVENTS_TO_COME' },]);

  const [searchWords, setSearchWords] = React.useState('');

  const getSearchWords = React.useCallback((currentSearchWords) => {
    setSearchWords(currentSearchWords);
  }, []);

  const getSelectedCategories = React.useCallback((selectedCategories) => {
    setCategories(selectedCategories);
  }, []);

  const makeUri = useCallback(
    (refObj) => {
      return `${websiteRoot}/${refObj}`.replace(/\/\/+/g, '/');
    },
    [websiteRoot]
  );

  const hitsToEvents = useCallback(
    (hits) => {
      return hits.map((h) => {
        return {
          ...h,
          slug: makeUri(h.refObjectPath),
        };
      });
    },
    [makeUri]
  );

  const getSelectedCategory = useCallback(() => {
    const o = {
      type: 'EVENTS_TO_COME',
      live: false,
    };
    if (categories) {
      categories.forEach((el) => {
        if (el.id === 'LIVE') {
          o['live'] = true;
        } else {
          o['type'] = el.id;
        }
      });
    }

    return o;
  }, [categories]);

  /**
   * Guide filter
   */
  const filterToAlgoliaQuery = useCallback(
    ({ type, live }) => {
      const qpts = [`type:"event-guide-event"  AND guideID: "${searchGuideID}"`,];
      const selectedCategory = type;

      const now = Math.floor(new Date().getTime() / 1000);
      qpts.push(
        ` end_date_timestamp ${
          selectedCategory !== 'EVENTS_TO_COME' ? ' < ' : ' >= '
        } ${now} `
      );
      if (live) {
        qpts.push(' live = 1 ');
      }
      return qpts.join(' AND ');
    },
    [searchGuideID]
  );

  const search = useCallback(
    (page, prev) => {
      const filterCriteria = getSelectedCategory();
      const indexName = indexNameBySearchType[filterCriteria.type];
      const index = client.initIndex(indexName);

      index
        .search(searchWords, {
          page: page,
          filters: filterToAlgoliaQuery(filterCriteria),
          hitsPerPage: events_per_page,
        })
        .then(({ hits, nbHits, page, nbPages }) => {
          hits = _.concat(prev, hits);
          setWebsiteEvents({
            hits: hitsToEvents(hits),
            nbHits: nbHits,
            nbPages: nbPages,
            page: page,
          });
        })
        .catch((err) => {
          console.error(err);
          return [];
        });
    },
    [
      client,
      events_per_page,
      filterToAlgoliaQuery,
      getSelectedCategory,
      hitsToEvents,
      indexNameBySearchType,
      searchWords,
    ]
  );

  // search engine number of hits
  const filter = !(
    categories?.length === 1 &&
    categories[0].id === 'EVENTS_TO_COME' &&
    searchWords === ''
  );
  useEffect(() => {
    if (filter) {
      search(0, []);
    } else {
      setWebsiteEvents({
        hits: bodyEvents,
      });
    }
  }, [categories, searchWords, bodyEvents, search, filter]);

  let resultElements = null;

  const setFocus = () => {
    if (resultElements && resultElements.length > 0) {
      resultElements[resultElements.length - 1].focus();
    }
  };

  if (typeof window !== 'undefined') {
    resultElements = document.getElementsByName('event-focus');
  }

  return (
    <Slice alias={'internal-page-layout'}>
      <EventGuideLayout
        filAriane={breadcrumbs}
        header={
          headerEvents?.length > 0 ? (
            <HeaderCarrousel
              carrousel
              color="RED"
              isEventGuide
              items={headerEvents}
              locale={locale}
              siteURL={websiteRoot}
            />
          ) : null
        }
        title={guide?.title || t('event.guide.page.title')}
      >
        <div className={eventsGuideStyles.eventsguideContainer}>
          {content && (
            <div
              className="ckContent"
              dangerouslySetInnerHTML={{
                __html: sanitizer(content, dompurifyConfig),
              }}
            >
            </div>
          )}
          <div
            className={eventsGuideStyles.fullSize}
            style={{ marginBottom: 20 }}
          >
            <h2 className={popUpStyles.title}>
              {t('event.guide.page.all.events')}
            </h2>
          </div>
          <div className={eventsGuideStyles.fullSize}>
            <div style={{ marginBottom: 30 }}>
              <SearchCriterias
                getSearchWordsOnClick={getSearchWords}
                getSelectedCategories={getSelectedCategories}
                locale={locale}
                switchDefault={bodyEvts?.length < 1}
              />
            </div>
            {websiteEvents?.hits?.length < 1 && (
              <p style={{ fontWeight: 900, fontSize: 20 }}>
                {t('event.guide.page.empty.results')}
              </p>
            )}
          </div>
          {websiteEvents?.hits?.length > 0 && (
            <Results display={display}
              events={websiteEvents.hits}
              locale={locale}
            />
          )}
        </div>
        {!filter && pagination?.totalPages > 1 && (
          <div style={{ marginTop: 50, marginBottom: 50 }}>
            <Pagination pagination={pagination}></Pagination>
          </div>
        )}
        {filter && websiteEvents?.page + 1 < websiteEvents?.nbPages && (
          <div className={popUpStyles.toutVoir}>
            <InfinitePagination
              label={t('nav.seeMore')}
              onclick={search}
              pageIndex={websiteEvents.page}
              prevItems={websiteEvents.hits}
              setFocus={setFocus}
            />
          </div>
        )}
      </EventGuideLayout>
    </Slice>
  );
}

export const query = graphql`
  query ($guideId: String!, $websiteId: String!, $bodyElementIds: [String!]) {
    cmsPortalEventGuide(
      id: { eq: $guideId }
      websites: { elemMatch: { id: { eq: $websiteId } } }
    ) {
      title
      id
      slug
      canonical_link
      content
      display
      locale
      events_per_page
      header {
        promoted_events {
          ...EventData
        }
        number_of_events
      }
      header_events {
        ...EventData
      }
    }
    events: allCmsPortalEvent(
      filter: { id: { in: $bodyElementIds } }
      sort: { start_date : ASC }
    ) {
      nodes {
        ...EventData
      }
    }
  }
`;
